import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, ExternalLink, Shield, Maximize2, X, ArrowLeft } from 'lucide-react';

/**
 * Composant d'intégration d'un microsite interactif officiel (gouvernemental).
 *
 * 2 modes d'affichage :
 *   - "inline"     : iframe intégrée dans la page (pour petits embeds compacts)
 *   - "fullscreen" : modale plein écran avec barre de contrôle (par défaut,
 *                    recommandé pour les simulateurs avec bannière cookies externe)
 *
 * Comportement commun :
 *   - Placeholder sans chargement externe tant que l'utilisateur ne clique pas
 *   - Crédit source officielle affiché
 *   - Lien "Ouvrir sur le site officiel" en complément
 */
export function SafeMicrositeEmbed({
  src,
  title = 'Microsite officiel',
  subtitle,
  sourceName = 'Sécurité routière',
  sourceUrl,
  posterUrl,
  mode = 'fullscreen',
  aspect = '16/9',
  height,
  testid = 'safe-microsite',
  className = '',
}) {
  const [loaded, setLoaded] = useState(false);

  // Bloquer le scroll du body quand la modale plein écran est ouverte
  useEffect(() => {
    if (mode === 'fullscreen' && loaded) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = originalOverflow; };
    }
  }, [loaded, mode]);

  // Fermer avec ESC en mode fullscreen
  useEffect(() => {
    if (mode === 'fullscreen' && loaded) {
      const onKey = (e) => { if (e.key === 'Escape') setLoaded(false); };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
  }, [loaded, mode]);

  const handleClose = () => setLoaded(false);

  const Placeholder = () => {
    const aspectClass = mode === 'inline' && !height
      ? (aspect === '4/3' ? 'aspect-[4/3]' : aspect === '1/1' ? 'aspect-square' : 'aspect-video')
      : 'aspect-video md:aspect-[21/9]';
    const heightStyle = mode === 'inline' && height ? { height } : {};

    return (
      <div
        className={`relative overflow-hidden rounded-xl border border-border ${aspectClass}`}
        style={heightStyle}
      >
        <div className="relative w-full h-full bg-[hsl(var(--panel))]">
          {posterUrl ? (
            <img
              src={posterUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div
              className="absolute inset-0 opacity-30"
              style={{
                background: 'radial-gradient(circle at 30% 30%, hsl(var(--primary) / 0.4), transparent 60%), radial-gradient(circle at 70% 70%, hsl(var(--primary) / 0.3), transparent 60%)',
              }}
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 sm:px-8 py-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 px-2.5 py-1 mb-3 text-[10px] font-black uppercase tracking-[0.18em]">
              <Shield className="w-3 h-3" />
              <span>Source officielle gouvernementale</span>
            </div>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-white mb-2 max-w-xl leading-tight">
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs sm:text-sm text-white/80 mb-5 max-w-md leading-relaxed">
                {subtitle}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setLoaded(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-dark))] text-white font-bold text-sm transition-all hover:scale-105 shadow-lg"
                data-testid={`${testid}-launch-btn`}
              >
                <Play className="w-4 h-4" fill="currentColor" />
                <span>Lancer l'expérience</span>
              </button>

              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs border border-white/20 transition-all"
                data-testid={`${testid}-external-btn`}
              >
                <Maximize2 className="w-3.5 h-3.5" />
                <span>Nouvel onglet</span>
                <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            </div>

            <p className="mt-4 text-[11px] text-white/60 max-w-md">
              {mode === 'fullscreen' ? (
                <>S'ouvre en <strong className="text-white/90">plein écran</strong> pour une meilleure lisibilité. Tu peux fermer à tout moment (bouton ← ou touche Échap).</>
              ) : (
                <>Aucune donnée transmise à <strong className="text-white/90">{sourceName}</strong> avant que tu cliques sur « Lancer ».</>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (mode === 'inline') {
    const aspectClass = !height ? (aspect === '4/3' ? 'aspect-[4/3]' : aspect === '1/1' ? 'aspect-square' : 'aspect-video') : '';
    const heightStyle = height ? { height } : {};

    if (!loaded) {
      return <div className={className} data-testid={testid}><Placeholder /></div>;
    }

    return (
      <div
        className={`relative overflow-hidden rounded-xl border border-border ${aspectClass} ${className}`}
        style={heightStyle}
        data-testid={testid}
      >
        <iframe
          src={src}
          title={title}
          className="absolute inset-0 w-full h-full"
          loading="lazy"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid={`${testid}-iframe`}
        />
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/80 hover:bg-black text-white text-[10px] font-bold uppercase tracking-wider border border-white/20 backdrop-blur-sm transition-colors"
          data-testid={`${testid}-close-inline`}
          aria-label="Fermer et revenir à Code 2 Roues"
        >
          <X className="w-3 h-3" />
          <span>Fermer</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={className} data-testid={testid}>
        <Placeholder />
      </div>

      {loaded && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-testid={`${testid}-modal`}
        >
          <div className="flex-shrink-0 h-14 sm:h-16 bg-[hsl(var(--background))] border-b-2 border-[hsl(var(--primary))]/30 flex items-center gap-2 px-3 sm:px-4 shadow-lg">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-dark))] text-white font-black text-xs sm:text-sm transition-colors flex-shrink-0 shadow-lg shadow-[hsl(var(--primary))]/30 ring-2 ring-[hsl(var(--primary))]/20"
              data-testid={`${testid}-close-modal`}
              aria-label="Fermer et revenir à Code 2 Roues"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Retour à Code 2 Roues</span>
              <span className="sm:hidden font-black uppercase tracking-wider">Retour</span>
            </button>

            <div className="flex-1 min-w-0 text-center hidden md:block">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                <Shield className="w-3 h-3" />
                <span>Source officielle · {sourceName}</span>
              </div>
              <p className="text-xs font-bold text-foreground truncate max-w-md mx-auto">
                {title}
              </p>
            </div>

            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[hsl(var(--panel))] hover:bg-[hsl(var(--panel))]/60 text-foreground text-xs font-bold border border-border transition-colors flex-shrink-0"
              data-testid={`${testid}-open-native`}
            >
              <Maximize2 className="w-3 h-3" />
              <span className="hidden sm:inline">Ouvrir en natif</span>
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>

            <button
              type="button"
              onClick={handleClose}
              className="w-9 h-9 rounded-full grid place-items-center bg-[hsl(var(--panel))] hover:bg-red-500/20 hover:text-red-400 text-foreground/80 border border-border transition-colors flex-shrink-0 md:hidden"
              data-testid={`${testid}-close-x`}
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 relative bg-white">
            <iframe
              src={src}
              title={title}
              className="absolute inset-0 w-full h-full border-0"
              loading="eager"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="fullscreen"
              data-testid={`${testid}-iframe`}
            />
          </div>

          <div className="flex-shrink-0 hidden sm:flex items-center justify-center gap-3 py-1.5 bg-[hsl(var(--background))] border-t border-border text-[10px] text-muted-foreground">
            <span>
              <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--panel))] border border-border text-foreground font-mono">Échap</kbd>
              {' '}pour fermer
            </span>
            <span>·</span>
            <span>Tu consultes {sourceName} · Retour immédiat via le bouton « Retour à Code 2 Roues »</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default SafeMicrositeEmbed;
